import {
  AWS_REGION_MARKETS,
  AwsRegions,
  Brand,
  BRAND_TO_SUPPORTED_REGIONS_MAPPING,
  ISO2_ISO_MAPPING,
  ISO_ISO2_MAPPING,
  IsoCountryCode,
  IsoCountryCode2Char,
  UPPER_CASE_COUNTRY_NAME_TO_ISO2_MAPPING,
} from '../types';

/**
 * Attempt to convert a string to a 2 character ISO country code
 * @param country Country in an unknown format
 * @returns ISO Country Code. Returns "undefined" if no country could be determined
 */
export function convertCountryStringToIsoCountryCode2Char(
  country: string,
): IsoCountryCode2Char | undefined {
  const normalizedCountry = country.toUpperCase().trim();

  // Country is in format "US"
  if (ISO2_ISO_MAPPING[normalizedCountry as IsoCountryCode2Char]) {
    return normalizedCountry as IsoCountryCode2Char;
  }

  // Country is in format "USA"
  if (ISO_ISO2_MAPPING[normalizedCountry as IsoCountryCode]) {
    return ISO_ISO2_MAPPING[normalizedCountry as IsoCountryCode];
  }

  return UPPER_CASE_COUNTRY_NAME_TO_ISO2_MAPPING[normalizedCountry];
}

/**
 * Given a brand and region, determine if the region is supported for the brand.
 *
 * @param {Brand} brand
 * The Brand.
 *
 * @param {IsoCountryCode2Char} region
 * A region.
 *
 * @returns {boolean}
 * true - The region is supported for the given brand.
 * false - The region is NOT supported for the given brand.
 */
export const isRegionSupportedForBrand = (brand: Brand, region: IsoCountryCode2Char): boolean =>
  BRAND_TO_SUPPORTED_REGIONS_MAPPING[brand]?.includes(region) ?? false;

/**
 * Given a market region, it returns the AWS region associated
 *
 * @param {IsoCountryCode2Char} region: market region
 *
 * @returns {AwsRegions} The AWS region associated to the market
 */
export const getAwsRegionByCountryCode = (region: IsoCountryCode2Char): AwsRegions => {
  const [awsRegion] = Object.entries(AWS_REGION_MARKETS).find(([, markets]) =>
    markets.includes(region),
  ) ?? [AwsRegions.USE1];
  return awsRegion as AwsRegions;
};

/**
 * Given a market region, it returns the AWS regions associated
 *
 * As we migrate, a market can be in two AWS regions at once. It's important to get all the regions
 * so the services can decide which one to use based on their own ENV vars
 * that define the regions the services are running in
 *
 * @param {IsoCountryCode2Char} region: market region
 *
 * @returns {AwsRegions} The AWS region associated to the market
 */
export const getAwsRegionsByCountryCode = (region: IsoCountryCode2Char): Array<AwsRegions> => {
  const awsRegions = Object.entries(AWS_REGION_MARKETS)
    .filter(([, markets]) => markets.includes(region))
    .map(([region]) => region) ?? [AwsRegions.USE1];
  return awsRegions as Array<AwsRegions>;
};

/**
 * Given a AWS region and a RBI market, it returns wether a RBI market runs from this AWS region
 *
 * @param {AwsRegions} awsRegion: region
 * @param {IsoCountryCode2Char} market: market
 *
 * @returns {boolean} Wether the market matches the region given
 */
export const isCountryCodeInAwsRegion = (
  awsRegion: AwsRegions,
  market: IsoCountryCode2Char,
): boolean => {
  const marketRegionMatch = Object.entries(AWS_REGION_MARKETS).filter(([region, markets]) => {
    return region === awsRegion && markets.includes(market);
  });
  return marketRegionMatch.length > 0;
};
